// File: app/src/utils/clockInput.js
//
// Helpers for the MM:SS masked input used by time-based performance
// capabilities (issue #18). The database stores `value` as a discrete NUMERIC
// (seconds), so the UI converts between the human MM:SS form and integer
// seconds at the edges:
//   - clockToSeconds("7:30") -> 450   (parse on submit; null on malformed)
//   - secondsToClock(450)    -> "7:30" (format on load)
//
// clockToSeconds is the validation safety net: it returns null (blocking
// submit) for partial / out-of-range / malformed input rather than NaN, so a
// value like "99:99" can never reach the API.

// Minutes 0–99, seconds 0–59, and optionally tenths, as an erg shows them
// ("6:44.4"). Single- or double-digit minutes are accepted.
const CLOCK_PATTERN = /^(\d{1,2}):([0-5]?\d)(?:\.(\d))?$/;

export function clockToSeconds(input) {
  if (input == null) return null;
  const str = String(input).trim();
  const match = str.match(CLOCK_PATTERN);
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  const tenths = match[3] ? Number(match[3]) : 0;
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
  if (minutes > 99 || seconds > 59) return null;
  return (minutes * 600 + seconds * 10 + tenths) / 10;
}

// `tenths` keeps a tenth of a second when there is one ("6:44.4"): for a time
// you entered. Worked-out times stay whole.
export function secondsToClock(totalSeconds, { tenths = false } = {}) {
  if (totalSeconds == null || totalSeconds === "") return "";
  const n = Number(totalSeconds);
  if (!Number.isFinite(n) || n < 0) return "";
  const t = tenths ? Math.round(n * 10) : Math.round(n) * 10;
  const minutes = Math.floor(t / 600);
  const seconds = Math.floor((t % 600) / 10);
  const tenth = t % 10;
  return `${minutes}:${String(seconds).padStart(2, "0")}${tenth ? `.${tenth}` : ""}`;
}

// Normalise raw keystrokes into a controlled MM:SS mask as the user types.
// Strips non-digits, caps at 4 digits (MMSS), clamps the seconds pair to 59,
// and inserts the colon once two seconds digits are present. A point after
// the seconds starts a tenth ("6:44.4"); a comma counts as one, for keyboards
// that show it. Used by the hand-rolled mask in PerformanceCapabilities so no
// input-mask dependency is needed.
export function formatClockInput(raw) {
  const [whole, ...rest] = String(raw ?? "").replace(/,/g, ".").split(".");
  const digits = whole.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  const minutes = digits.slice(0, digits.length - 2);
  let seconds = digits.slice(digits.length - 2);
  if (Number(seconds) > 59) seconds = "59";
  const clock = `${minutes}:${seconds}`;
  if (!rest.length) return clock;
  return `${clock}.${rest.join("").replace(/\D/g, "").slice(0, 1)}`;
}
